/**
 * Session and membership. `AuthProvider` tracks the supabase-js session; `AuthGate`
 * (pages/auth/AuthGate.tsx) uses `useMember()` to decide what a signed-in user may see.
 */
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { Session } from '@supabase/supabase-js';
import { useQuery } from '@tanstack/react-query';
import { supabase } from './supabase';
import { queryClient, setAppGate, toSbError, type SbError } from './query';
import type { Tables } from './database.types';

export type Member = Tables<'members'>;
export type Role = 'admin' | 'editor' | 'viewer';

type AuthState = {
  /** undefined while supabase-js is still reading storage or the URL. */
  session: Session | null | undefined;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null | undefined>(undefined);

  useEffect(() => {
    let alive = true;
    supabase.auth.getSession().then(({ data }) => {
      if (alive) setSession(data.session ?? null);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((event, next) => {
      setSession(next ?? null);
      if (event === 'SIGNED_OUT') {
        queryClient.clear();
        setAppGate(null);
      }
    });
    return () => {
      alive = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      session,
      signOut: async () => {
        await supabase.auth.signOut({ scope: 'local' });
        queryClient.clear();
        setAppGate(null);
        setSession(null);
      },
    }),
    [session],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}

/** The signed-in user's id and email, or null when signed out. */
export function useSessionUser(): { id: string; email: string } | null {
  const { session } = useAuth();
  if (!session?.user) return null;
  return { id: session.user.id, email: session.user.email ?? '' };
}

/** Users whose membership was claimed in this page load. claim_membership() runs once per user. */
const claimed = new Set<string>();

/**
 * The current user's `seo.members` row, or null when the account is not on the allowlist.
 * First calls `claim_membership()` (once per user per page load), which links an invited email
 * to this auth user on first login (Task 1); then loads the row by `user_id`.
 * Cached for the session; key ['seo', 'members', 'me', uid]. Invalidating ['seo', 'members']
 * reloads the row without claiming again.
 */
export function useMember() {
  const user = useSessionUser();
  const uid = user?.id ?? null;
  return useQuery<Member | null, SbError>({
    queryKey: ['seo', 'members', 'me', uid],
    enabled: !!uid,
    staleTime: Number.POSITIVE_INFINITY,
    queryFn: async () => {
      let claimedRow: Member | null = null;
      if (!claimed.has(uid!)) {
        const claim = await supabase.rpc('claim_membership');
        if (claim.error) throw toSbError(claim.error);
        claimed.add(uid!);
        claimedRow = claim.data ?? null;
      }
      const { data, error } = await supabase.from('members').select('*').eq('user_id', uid!).maybeSingle();
      if (error) throw toSbError(error);
      return data ?? (claimedRow?.user_id === uid ? claimedRow : null);
    },
  });
}

const NEXT_KEY = 'seohq.next';

/** Remembers where to go after the magic link signs the user in. Only same-site paths. */
export function rememberNext(path: string | null | undefined) {
  try {
    if (path && path.startsWith('/') && !path.startsWith('//') && !path.startsWith('/login') && !path.startsWith('/auth/')) {
      localStorage.setItem(NEXT_KEY, path);
    } else {
      localStorage.removeItem(NEXT_KEY);
    }
  } catch {
    /* storage blocked */
  }
}

/** The remembered path, or "/". Side-effect free, so it is safe to call during render. */
export function peekNext(): string {
  try {
    const v = localStorage.getItem(NEXT_KEY);
    if (v && v.startsWith('/') && !v.startsWith('//') && !v.includes('\\')) return v;
  } catch {
    /* storage blocked */
  }
  return '/';
}

/** Forgets the remembered path (call from an effect once it has been used). */
export function clearNext() {
  try {
    localStorage.removeItem(NEXT_KEY);
  } catch {
    /* storage blocked */
  }
}

export const ROLE_RANK: Record<Role, number> = { viewer: 1, editor: 2, admin: 3 };

/** True when `role` is at least `min`. */
export function hasRole(role: Role | null | undefined, min: Role): boolean {
  return !!role && ROLE_RANK[role] >= ROLE_RANK[min];
}
